import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import api from '@/services/api'

export const useProductsStore = defineStore('products', () => {
    // State inizializzato vuoto, viene popolato dal database
    const products = ref([])
    const categories = ref([])
    const currentProduct = ref(null)
    const isLoading = ref(false)
    const filters = ref({
        category: null,
        search: '',
        available: true,
        priceRange: [0, 1000],
        sortBy: 'name',
        sortOrder: 'asc'
    })
    const pagination = ref({
        page: 1,
        perPage: 20,
        total: 0,
        totalPages: 0
    })

    const toast = useToast()

    // Getters
    const filteredProducts = computed(() => {
        // Controllo di sicurezza per products.value
        if (!products.value || !Array.isArray(products.value)) {
            return []
        }

        let result = [...products.value]

        // Filtra per categoria
        if (filters.value.category) {
            result = result.filter(p => p.category_id === filters.value.category)
        }

        // Filtra per ricerca
        if (filters.value.search) {
            const searchLower = filters.value.search.toLowerCase()
            result = result.filter(p =>
                p.name?.toLowerCase().includes(searchLower) ||
                p.description?.toLowerCase().includes(searchLower)
            )
        }

        // Filtra per disponibilità
        if (filters.value.available !== null) {
            result = result.filter(p => p.active === filters.value.available)
        }

        // Filtra per range prezzo
        result = result.filter(p =>
            p.price_base >= filters.value.priceRange[0] &&
            p.price_base <= filters.value.priceRange[1]
        )

        // Ordina
        result.sort((a, b) => {
            let comparison = 0
            switch (filters.value.sortBy) {
                case 'name':
                    comparison = (a.name || '').localeCompare(b.name || '')
                    break
                case 'price':
                    comparison = (a.price_base || 0) - (b.price_base || 0)
                    break
                case 'category':
                    comparison = (a.category?.name || '').localeCompare(b.category?.name || '')
                    break
                case 'created':
                    comparison = new Date(b.created_at) - new Date(a.created_at)
                    break
                default:
                    comparison = 0
            }
            return filters.value.sortOrder === 'asc' ? comparison : -comparison
        })

        return result
    })

    const productsForCategory = (categoryId) => {
        if (!products.value || !Array.isArray(products.value)) {
            return []
        }
        return products.value.filter(p => p.category_id === categoryId && p.active)
    }

    const productsByIds = (ids) => {
        if (!products.value || !Array.isArray(products.value)) {
            return []
        }
        return products.value.filter(p => ids.includes(p.id))
    }

    const featuredProducts = computed(() => {
        if (!products.value || !Array.isArray(products.value)) {
            return []
        }
        return products.value.filter(p => p.featured && p.active).slice(0, 8)
    })

    const newProducts = computed(() => {
        if (!products.value || !Array.isArray(products.value)) {
            return []
        }
        return [...products.value]
            .filter(p => p.active)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 8)
    })

    // Actions
    const fetchProducts = async (params = {}) => {
        isLoading.value = true
        try {
            console.log('Fetching products from API...')
            const response = await api.get('/products', {
                params: {
                    page: pagination.value.page,
                    per_page: pagination.value.perPage,
                    ...filters.value,
                    ...params
                }
            })

            console.log('Products API response:', response.data)

            if (response.data && response.data.products && Array.isArray(response.data.products)) {
                // Formato con metadata di paginazione
                products.value = response.data.products.map(product => ({
                    ...product,
                    // Normalize is_active to active for compatibility
                    active: product.is_active !== undefined ? product.is_active : product.active,
                    // Assicurati che tutti i campi necessari siano presenti
                    inStock: product.stock > 0,
                    onSale: product.sale_price && product.sale_price < product.price,
                    price_base: product.price_base || product.price
                }))

                pagination.value = {
                    page: response.data.page || 1,
                    perPage: response.data.per_page || 20,
                    total: response.data.total || response.data.products.length,
                    totalPages: response.data.total_pages || 1
                }
            } else if (Array.isArray(response.data)) {
                // Formato semplice - array di prodotti
                products.value = response.data.map(product => ({
                    ...product,
                    // Normalize is_active to active for compatibility
                    active: product.is_active !== undefined ? product.is_active : product.active,
                    inStock: product.stock > 0,
                    onSale: product.sale_price && product.sale_price < product.price,
                    price_base: product.price_base || product.price
                }))

                pagination.value = {
                    page: 1,
                    perPage: response.data.length,
                    total: response.data.length,
                    totalPages: 1
                }
            } else {
                console.error('Invalid products data format:', response.data)
                products.value = []
            }

            console.log('Products loaded:', products.value.length, 'items')
            return true
        } catch (error) {
            console.error('Fetch products error:', error)
            toast.error('Errore nel caricamento dei prodotti')
            products.value = []
            return false
        } finally {
            isLoading.value = false
        }
    }

    const totalProducts = computed(() => pagination.value.total || products.value.length)

    const fetchProduct = async (id) => {
        isLoading.value = true
        try {
            const response = await api.get(`/products/${id}`)

            // Normalize is_active to active for compatibility
            const normalizedProduct = {
                ...response.data,
                active: response.data.is_active !== undefined ? response.data.is_active : response.data.active
            }

            currentProduct.value = normalizedProduct
            return normalizedProduct
        } catch (error) {
            console.error('Fetch product error:', error)
            toast.error('Errore nel caricamento del prodotto')
            return null
        } finally {
            isLoading.value = false
        }
    }

    const fetchCategories = async () => {
        try {
            console.log('Fetching categories from API...')
            const response = await api.get('/categories')
            console.log('Categories API response:', response.data)

            const rawCategories = response.data.categories || response.data || []

            if (Array.isArray(rawCategories)) {
                categories.value = rawCategories.map(category => ({
                    ...category,
                    // Normalize is_active to active for compatibility
                    active: category.is_active !== undefined ? category.is_active : category.active,
                    count: 0 // Placeholder per il numero di prodotti, verrà calcolato nel frontend
                }))
            } else {
                categories.value = []
            }

            console.log('Categories loaded:', categories.value.length, 'items')
            return true
        } catch (error) {
            console.error('Fetch categories error:', error)
            toast.error('Errore nel caricamento delle categorie')
            return false
        }
    }

    const createProduct = async (productData) => {
        isLoading.value = true
        try {
            // Separate image file from product data
            const imageFile = productData.image
            const dataToSend = { ...productData }
            delete dataToSend.image
            delete dataToSend.imagePreview

            const response = await api.post('/products', dataToSend)

            // Upload image if provided
            let imageUrl = null
            if (imageFile && response.data.product_id) {
                imageUrl = await uploadProductImage(response.data.product_id, imageFile)
            }

            // Fetch the complete product with image
            const productResponse = await api.get(`/products/${response.data.product_id}`)

            // Normalize is_active to active for compatibility
            const normalizedProduct = {
                ...productResponse.data,
                active: productResponse.data.is_active !== undefined ? productResponse.data.is_active : productResponse.data.active
            }

            products.value.unshift(normalizedProduct)
            toast.success('Prodotto creato con successo')
            return normalizedProduct
        } catch (error) {
            console.error('Create product error:', error)
            toast.error(error.response?.data?.message || 'Errore nella creazione del prodotto')
            return null
        } finally {
            isLoading.value = false
        }
    }

    const updateProduct = async (id, productData) => {
        console.log('[STORE] updateProduct called with id:', id, 'data:', productData)
        isLoading.value = true
        try {
            // Separate image file from product data
            const imageFile = productData.image
            const dataToSend = { ...productData }
            delete dataToSend.image
            delete dataToSend.imagePreview

            console.log('[STORE] Sending PUT request with data:', dataToSend)
            const response = await api.put(`/products/${id}`, dataToSend)
            console.log('[STORE] PUT response:', response.data)

            // Upload image if provided (new file uploaded)
            if (imageFile && typeof imageFile !== 'string') {
                console.log('[STORE] Uploading image for product:', id)
                await uploadProductImage(id, imageFile)
            } else {
                console.log('[STORE] No new image to upload')
            }

            // Fetch the complete updated product
            console.log('[STORE] Fetching updated product:', id)
            const productResponse = await api.get(`/products/${id}`)
            console.log('[STORE] Product fetched:', productResponse.data)

            // Normalize is_active to active for compatibility
            const normalizedProduct = {
                ...productResponse.data,
                active: productResponse.data.is_active !== undefined ? productResponse.data.is_active : productResponse.data.active
            }

            const index = products.value.findIndex(p => p.id === id)
            if (index !== -1) {
                products.value[index] = normalizedProduct
            }
            if (currentProduct.value?.id === id) {
                currentProduct.value = normalizedProduct
            }
            toast.success('Prodotto aggiornato con successo')
            return normalizedProduct
        } catch (error) {
            console.error('Update product error:', error)
            toast.error(error.response?.data?.message || 'Errore nell\'aggiornamento del prodotto')
            return null
        } finally {
            isLoading.value = false
        }
    }

    const deleteProduct = async (id) => {
        isLoading.value = true
        try {
            await api.delete(`/products/${id}`)
            products.value = products.value.filter(p => p.id !== id)
            toast.success('Prodotto eliminato con successo')
            return true
        } catch (error) {
            console.error('Delete product error:', error)
            toast.error(error.response?.data?.message || 'Errore nell\'eliminazione del prodotto')
            return false
        } finally {
            isLoading.value = false
        }
    }

    const duplicateProduct = async (id) => {
        isLoading.value = true
        try {
            const response = await api.post(`/products/${id}/duplicate`)

            // Normalize is_active to active for compatibility
            const normalizedProduct = {
                ...response.data,
                active: response.data.is_active !== undefined ? response.data.is_active : response.data.active
            }

            products.value.unshift(normalizedProduct)
            toast.success('Prodotto duplicato con successo')
            return normalizedProduct
        } catch (error) {
            console.error('Duplicate product error:', error)
            toast.error(error.response?.data?.message || 'Errore nella duplicazione del prodotto')
            return null
        } finally {
            isLoading.value = false
        }
    }

    const toggleProductStatus = async (id) => {
        const product = products.value.find(p => p.id === id)
        if (product) {
            const newStatus = !product.active
            const updated = await updateProduct(id, { active: newStatus })
            if (updated) {
                toast.success(newStatus ? 'Prodotto attivato' : 'Prodotto disattivato')
            }
            return updated
        }
        return null
    }

    const uploadProductImage = async (productId, file) => {
        const formData = new FormData()
        formData.append('image', file)

        try {
            const response = await api.post(`/products/${productId}/image`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            toast.success('Immagine caricata con successo')
            return response.data.image_url
        } catch (error) {
            console.error('Upload image error:', error)
            toast.error('Errore nel caricamento dell\'immagine')
            return null
        }
    }

    const setFilters = (newFilters) => {
        filters.value = { ...filters.value, ...newFilters }
        pagination.value.page = 1 // Reset alla prima pagina quando cambiano i filtri
    }

    const resetFilters = () => {
        filters.value = {
            category: null,
            search: '',
            available: true,
            priceRange: [0, 1000],
            sortBy: 'name',
            sortOrder: 'asc'
        }
        pagination.value.page = 1
    }

    const changePage = (page) => {
        pagination.value.page = page
        fetchProducts()
    }

    const fetchFeaturedProducts = async () => {
        isLoading.value = true
        try {
            // Carichiamo direttamente tutti i prodotti attivi
            const response = await api.get('/products', {
                params: {
                    active: true,
                    per_page: 50
                }
            })

            if (response.data && response.data.products) {
                products.value = response.data.products.map(product => ({
                    ...product,
                    // Normalize is_active to active for compatibility
                    active: product.is_active !== undefined ? product.is_active : product.active
                }))
            } else if (Array.isArray(response.data)) {
                products.value = response.data.map(product => ({
                    ...product,
                    // Normalize is_active to active for compatibility
                    active: product.is_active !== undefined ? product.is_active : product.active
                }))
            } else {
                products.value = []
            }

            return true
        } catch (error) {
            console.error('Error loading products:', error)
            products.value = []
            return false
        } finally {
            isLoading.value = false
        }
    }

    return {
        // State
        products,
        categories,
        currentProduct,
        isLoading,
        filters,
        pagination,

        // Getters
        filteredProducts,
        productsForCategory,
        productsByIds,
        featuredProducts,
        newProducts,
        totalProducts,

        // Actions
        fetchProducts,
        fetchProduct,
        fetchCategories,
        createProduct,
        updateProduct,
        deleteProduct,
        duplicateProduct,
        toggleProductStatus,
        uploadProductImage,
        setFilters,
        resetFilters,
        changePage,
        fetchFeaturedProducts
    }
}, {
    persist: {
        paths: ['filters']
    }
})
